// @ts-nocheck

import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { Target } from '../../../../../client';
import SpinePlayer from '../../../../../components/SpinePlayer';
import Spritesheet from '../../../../../components/Spritesheet';
import { BoomAssetsBySkinId } from '../../../constants/assets';
import BoomUIBySkinId, { BoomSkinIdWithoutIdleAnimation } from '../../../constants/ui';
import { useBoomViewContext } from '../../../contexts/BoomViewContext';

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  position: relative;

  ${({ $skinId }) => {
    const wrapperStyle = BoomUIBySkinId[$skinId]?.ITEM_WRAPPER;

    if (wrapperStyle) {
      return Target.__MOBILE__
        ? css`
            margin-top: ${wrapperStyle.marginTop}em;
          `
        : css`
            margin-top: ${wrapperStyle.marginTop / 2}em;
          `;
    }
  }}

  ${({ $isSmall, $hasAnimation }) =>
    !$isSmall
      ? css`
          transform: ${$hasAnimation ? 'scale(1.3)' : 'scale(1.2)'};
        `
      : css`
          margin-bottom: 2em;
        `}
`;

const StyledSpritesheet = styled(Spritesheet)`
  position: absolute;
  bottom: 13%;
  left: 50%;
  transform: translate(-50%, 5%) scale(1.6);
`;

const BoomBombsImg = styled.img`
  position: absolute;
  left: 50%;
  transform: translate(-50%, 5%) scale(1.6);
  width: ${({ $isSmall }) => ($isSmall ? '10em' : Target.__MOBILE__ ? '18em' : '14em')};
  bottom: ${({ $isSmall }) => ($isSmall ? '0%' : '13%')};
`;

const BoomItemImg = styled.div`
  position: relative;
  background-image: url(${({ $src }) => $src});
  background-repeat: no-repeat;
  background-size: contain;
  background-position: center;
  width: ${({ $isSmall }) => ($isSmall ? '10em' : Target.__MOBILE__ ? '18em' : '16em')};
  height: ${({ $isSmall }) => ($isSmall ? '10em' : Target.__MOBILE__ ? '18em' : '16em')};
`;

const StyledSpinePlayer = styled(SpinePlayer)`
  z-index: -1;

  position: relative;
  padding-top: ${Target.__MOBILE__ ? undefined : '0'};

  ${({ $skinId }) => {
    const wrapperStyle = BoomUIBySkinId[$skinId]?.ITEM_WRAPPER;

    if (wrapperStyle) {
      return Target.__MOBILE__
        ? css`
            margin-bottom: -${wrapperStyle.marginTop}em;
          `
        : css`
            margin-bottom: -${wrapperStyle.marginTop / 2}em;
          `;
    }
  }}

  width: ${({ $isSmall }) => ($isSmall ? '10em' : Target.__MOBILE__ ? '18em' : '16em')};
  height: ${({ $isSmall }) => ($isSmall ? '10em' : Target.__MOBILE__ ? '18em' : '16em')};
`;

const FogAnimation = keyframes`
  0% {
    transform: translateX(-50%)
  }
  100% {
    transform: translateX(0%)
  }
`;

const FogContainer = styled.div`
  position: absolute;
  left: -50%;
  top: ${Target.__MOBILE__ ? '75%' : '60%'};

  filter: brightness(1.5);
  opacity: 0.9;
  display: flex;
  align-items: center;
  justify-content: center;

  animation: 60s linear ${FogAnimation} infinite;
`;

const AnimatedFog = styled.div`
  position: relative;

  height: 18em;
  ${({ $srcImg }) => css`
    background-image: url(${$srcImg});
  `}

  background-repeat: no-repeat;
  background-size: contain;

  ${({ $availableWidth }) => css`
    width: ${2 * $availableWidth}px;
  `}
`;

const Fog = styled.div`
  position: absolute;
  left: -50%;
  top: 55%
  height: 18em;
  ${({ $srcImg }) => css`
    background-image: url(${$srcImg});
  `}

  background-repeat: no-repeat;
  background-size: cover;

  ${({ $availableWidth }) => css`
    width: ${2 * $availableWidth}px;
  `}
`;

type PropsType = {
  skinId: string;
  nbWonSteps: number;
  isStepView?: boolean;
  showAnimation?: boolean;
};

const BoomItem = ({ skinId, nbWonSteps, isStepView = false, showAnimation = false }: PropsType) => {
  const imgRef = useRef(null);
  const [imgWidth, setImgWidth] = useState(0);
  const { width: availableWidth } = useBoomViewContext();

  useEffect(() => {
    if (imgRef.current?.clientWidth) {
      setImgWidth(imgRef.current?.clientWidth);
    }
  }, [imgRef.current?.clientWidth, isStepView]);

  // TODO: to refactor (in Container ?)
  const imgSrc = useMemo(
    () => BoomAssetsBySkinId[skinId]?.StepIconsWithBallByStepId[`STEP_${nbWonSteps + 1}`],
    [skinId, nbWonSteps],
  );

  const fixImgSrc = useMemo(
    () => BoomAssetsBySkinId[skinId]?.StepIconsByStepId[`STEP_${nbWonSteps + 1}`],
    [skinId, nbWonSteps],
  );

  const animationSrc = useMemo(
    () => BoomAssetsBySkinId[skinId]?.StepAnimationByStepId[`STEP_${nbWonSteps + 1}`],
    [skinId, nbWonSteps],
  );

  const sparkAnimationSrc = useMemo(
    () => BoomAssetsBySkinId[skinId]?.StepSparkAnimationByStepId[`STEP_${nbWonSteps + 1}`],
    [skinId, nbWonSteps],
  );

  const hasIdleAnimation = useMemo(() => BoomSkinIdWithoutIdleAnimation.indexOf(skinId) === -1, [skinId]);

  const fogSrcImg = useMemo(() => BoomAssetsBySkinId[skinId]?.FogImgPath, [skinId]);

  const hasFog = useMemo(() => !isStepView && !!fogSrcImg, [isStepView, fogSrcImg]);

  const SpinerAnimationRatio = Target.__MOBILE__ ? 0.55 : 0.45;

  return (
    <Wrapper
      data-testid={`step-img-${nbWonSteps}`}
      $isSmall={isStepView}
      $hasAnimation={hasIdleAnimation}
      $skinId={skinId}
    >
      {hasIdleAnimation ? (
        <>
          {showAnimation ? (
            <StyledSpinePlayer
              $skinId={skinId}
              $isSmall={isStepView}
              animationId={animationSrc}
              scale={SpinerAnimationRatio}
              hideMuteButton
            />
          ) : (
            <BoomItemImg $src={fixImgSrc} $isSmall={isStepView} ref={imgRef} />
          )}
          {hasFog ? (
            <FogContainer>
              <AnimatedFog $srcImg={fogSrcImg} $availableWidth={availableWidth} />
              <AnimatedFog $srcImg={fogSrcImg} $availableWidth={availableWidth} />
            </FogContainer>
          ) : null}
          <BoomBombsImg src={imgSrc} $isSmall={isStepView} ref={imgRef} />
        </>
      ) : (
        <BoomItemImg $src={imgSrc} $isSmall={isStepView} ref={imgRef}>
          {hasFog ? <Fog $srcImg={fogSrcImg} $availableWidth={availableWidth} /> : null}
        </BoomItemImg>
      )}

      {sparkAnimationSrc && showAnimation ? (
        <StyledSpritesheet
          columnCount={BoomAssetsBySkinId[skinId]?.StepSparkAnimationSettings.columnCount}
          duration={BoomAssetsBySkinId[skinId]?.StepSparkAnimationSettings.duration}
          keepRatio
          play
          loop
          rowCount={BoomAssetsBySkinId[skinId]?.StepSparkAnimationSettings.rowCount}
          url={sparkAnimationSrc}
          width={imgWidth}
          expires
          $bottom={hasIdleAnimation}
        />
      ) : null}
    </Wrapper>
  );
};

export default BoomItem;
